import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { QUERY_PARAMS, ROUTES } from '@shared/config';
import { AuthenticationService } from '@security/authentication.service';
import { SessionService } from '@security/session.service';
import { SessionStorageService } from '@shared/services/session-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthGuardService implements CanActivate {
  constructor(
    private readonly session: SessionService,
    private readonly authenticationService: AuthenticationService,
    private router: Router
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.session
      .resolve()
      .pipe(
        map(authenticated =>
          authenticated
            ? this.authenticationService.verifyAppAccess() &&
              this.restoreUrlBeforeRedirect(state)
            : this.failToLogin(state)
        )
      );
  }

  /**
   * MSAL redirects are forced to return to the app base URL (see
   * MsalAuthenticationService.withBaseRedirectStartPage); once access is
   * granted, resume the route the user was on when the redirect started.
   */
  private restoreUrlBeforeRedirect(
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    const returnUrl = SessionStorageService.getMsalReturnUrl();
    if (!returnUrl) {
      return true;
    }

    SessionStorageService.clearMsalReturnUrl();

    if (returnUrl === state.url) {
      return true;
    }

    return this.router.parseUrl(returnUrl);
  }

  private failToLogin(state: RouterStateSnapshot): false {
    this.router.navigate([ROUTES.login], {
      queryParams: { [QUERY_PARAMS.returnUrl]: state.url },
      replaceUrl: true,
    });
    return false;
  }
}
